import { useMemo } from 'react'
import fetch from 'isomorphic-fetch'
import { ApolloClient, InMemoryCache, HttpLink, from } from '@apollo/client'

import { getToken, getUserData } from './tokenStorage'
import introspectionQueryResultData from './fragmentTypes.json'

let apolloClient

const fetchWithTimeout = (url, options, timeout = 70000) => {
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), timeout)

  return fetch(url, {
    ...options,
    signal: controller.signal,
  }).finally(() => clearTimeout(id))
}

const customFetch = (uri: string, options) => {
  let type = 'public'
  const token = getToken()

  if (token) {
    const userData = getUserData(token)
    if (userData?.type === 'Customer') type = 'customer'
    if (userData?.type === 'Breeder') type = 'breeder'
    if (userData?.type === 'Admin') type = 'admin'
  }

  const endpoint = `${uri}/graphql/${type}`

  return fetchWithTimeout(endpoint, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token ?? ''}`,
    },
  })
}

function createApolloClient() {
  const httpLink = new HttpLink({
    uri: process.env.NEXT_PUBLIC_GQL_HOST,
    fetch: customFetch,
  })

  const possibleTypes = {}
  introspectionQueryResultData.__schema.types.forEach((t) => {
    if (t.possibleTypes) {
      possibleTypes[t.name] = t.possibleTypes.map((st) => st.name)
    }
  })

  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: from([httpLink]),
    cache: new InMemoryCache({ possibleTypes }),
    defaultOptions: {
      query: { fetchPolicy: 'no-cache' },
    },
  })
}

export function initializeApollo(initialState = null) {
  const _apolloClient = apolloClient ?? createApolloClient()

  if (initialState) {
    _apolloClient.cache.restore(initialState)
  }

  if (typeof window === 'undefined') return _apolloClient
  if (!apolloClient) apolloClient = _apolloClient

  return _apolloClient
}

export function useApollo(initialState) {
  return useMemo(() => initializeApollo(initialState), [initialState])
}
